import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  MessageSquare, Send, Users, CreditCard, Phone, Shield, ChevronRight,
  Plus, Loader2, CheckCircle, Clock, AlertCircle, BarChart3, FileText,
  Trash2, Edit2, X, Tag, Search, Zap, DollarSign, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientSMS({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [view, setView] = useState('overview');
  const [stats, setStats] = useState(null);

  const hdrs = useCallback(() => ({
    ...getHeaders(),
    'Content-Type': 'application/json',
  }), [getHeaders]);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/sms/stats`, { headers: getHeaders() })
      .then(r => r.json()).then(setStats).catch(console.error);
  }, [getHeaders, view]);

  return (
    <div className="space-y-5" data-testid="sms-tab">
      <div className="flex gap-1 bg-white rounded-lg p-1 border border-gray-200 overflow-x-auto">
        {[
          { id: 'overview', label: t('tabs.overview'), icon: BarChart3 },
          { id: 'inbox', label: t('sms.tabInbox'), icon: MessageSquare },
          { id: 'send', label: t('sms.tabSend'), icon: Send },
          { id: 'contacts', label: t('sms.tabContacts'), icon: Users },
          { id: 'templates', label: t('sms.tabTemplates'), icon: FileText },
          { id: 'optin', label: t('sms.tabOptin'), icon: Shield },
          { id: '10dlc', label: t('sms.tab10dlc'), icon: FileText },
        ].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
              view === v.id ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:text-gray-900 hover:bg-white/5'
            }`} data-testid={`sms-view-${v.id}`}>
            <v.icon className="h-3.5 w-3.5" /> {v.label}
          </button>
        ))}
      </div>

      {view === 'overview' && <SMSOverview stats={stats} client={client} />}
      {view === 'inbox' && <InboxView getHeaders={getHeaders} hdrs={hdrs} />}
      {view === 'send' && <SendView getHeaders={getHeaders} hdrs={hdrs} />}
      {view === 'contacts' && <ContactsView getHeaders={getHeaders} hdrs={hdrs} />}
      {view === 'templates' && <TemplatesView getHeaders={getHeaders} hdrs={hdrs} />}
      {view === 'optin' && <OptInView getHeaders={getHeaders} hdrs={hdrs} />}
      {view === '10dlc' && <TenDLCView getHeaders={getHeaders} hdrs={hdrs} />}
    </div>
  );
}

function SMSOverview({ stats, client }) {
  const [showRecharge, setShowRecharge] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [autoRecharge, setAutoRecharge] = useState(null);
  const [savingAuto, setSavingAuto] = useState(false);

  const loadAutoRecharge = useCallback(async () => {
    if (!client?.slug) return;
    try {
      const res = await fetch(`${API}/api/client/sms/billing/auto-recharge?slug=${client.slug}`);
      if (res.ok) setAutoRecharge(await res.json());
    } catch (e) { console.error(e); }
  }, [client?.slug]);

  useEffect(() => { loadAutoRecharge(); }, [loadAutoRecharge]);

  const loadTiers = async () => {
    setLoadingTiers(true);
    try {
      const res = await fetch(`${API}/api/client/sms/billing/recharge-tiers`);
      if (res.ok) {
        const data = await res.json();
        setTiers(data.tiers || []);
        if (data.tiers?.length) setSelectedTier(data.tiers[0].id);
      }
    } catch (e) { console.error(e); }
    setLoadingTiers(false);
  };

  const openRecharge = () => {
    setShowRecharge(true);
    if (!tiers.length) loadTiers();
  };

  const handleCheckout = async (method = 'stripe') => {
    if (!selectedTier || !client?.slug) return;
    setProcessing(true);
    try {
      const endpoint = method === 'paypal'
        ? `${API}/api/client/sms/billing/recharge-paypal`
        : `${API}/api/client/sms/billing/recharge-checkout`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: client.slug,
          tier_id: selectedTier,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert(data.detail || 'Failed to create checkout session');
    } catch (e) { console.error(e); alert('Checkout failed'); }
    setProcessing(false);
  };

  const handleSetupAutoRecharge = async () => {
    if (!client?.slug) return;
    setSavingAuto(true);
    try {
      const res = await fetch(`${API}/api/client/sms/billing/auto-recharge/setup-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: client.slug, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert(data.detail || 'Failed to setup card');
    } catch (e) { console.error(e); alert('Setup failed'); }
    setSavingAuto(false);
  };

  const updateAutoRecharge = async (updates) => {
    if (!client?.slug) return;
    setSavingAuto(true);
    try {
      const res = await fetch(`${API}/api/client/sms/billing/auto-recharge`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: client.slug, ...updates }),
      });
      if (res.ok) {
        const data = await res.json();
        setAutoRecharge(data);
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update');
      }
    } catch (e) { console.error(e); }
    setSavingAuto(false);
  };

  if (!stats) return <div className="text-gray-400 animate-pulse text-center py-12">Loading...</div>;

  const balance = stats.balance || 0;
  const isLow = balance < 5;

  const cards = [
    { label: 'Messages Sent', value: stats.total_sent, icon: Send, color: 'text-blue-400 bg-blue-900/30' },
    { label: 'Messages Received', value: stats.total_received, icon: MessageSquare, color: 'text-green-400 bg-green-50' },
    { label: 'SMS Balance', value: `$${balance.toFixed(2)}`, icon: CreditCard, color: isLow ? 'text-red-400 bg-red-50' : 'text-cyan-400 bg-cyan-900/30' },
    { label: 'SMS Remaining', value: stats.sms_remaining, icon: Phone, color: 'text-amber-400 bg-amber-900/30' },
    { label: 'Verified Subscribers', value: stats.verified_subscribers, icon: Shield, color: 'text-purple-400 bg-purple-900/30' },
  ];

  const selected = tiers.find(t => t.id === selectedTier);

  return (
    <div data-testid="sms-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <Card key={c.label} className="bg-white border-gray-200">
            <CardContent className="pt-4 pb-4">
              <div className={`p-2 rounded-xl ${c.color} w-fit mb-2`}><c.icon className="h-4 w-4" /></div>
              <p className="text-xl font-bold text-gray-900">{c.value}</p>
              <p className="text-[10px] text-gray-500">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Low Balance Banner with Recharge CTA */}
      {isLow && (
        <div className="mt-4 bg-red-900/20 border border-red-500/30 rounded-xl p-4 flex items-center justify-between" data-testid="low-balance-banner">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-4.5 w-4.5 text-red-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-red-300">
                {balance <= 0 ? 'SMS balance is empty' : 'SMS balance is low'}
              </p>
              <p className="text-[11px] text-red-400/70">
                {balance <= 0
                  ? 'You cannot send messages until you recharge.'
                  : `Only $${balance.toFixed(2)} remaining — recharge now to avoid disruption.`}
              </p>
            </div>
          </div>
          <Button onClick={openRecharge} className="bg-red-600 hover:bg-red-700 text-xs h-9 px-4 flex-shrink-0" data-testid="recharge-cta-btn">
            <Zap className="h-3.5 w-3.5 mr-1.5" /> Recharge Now
          </Button>
        </div>
      )}

      {/* SMS Billing Explainer + Recharge Button */}
      <div className="mt-4 bg-[#0d1b2a] border border-cyan-900/30 rounded-lg px-4 py-3" data-testid="sms-billing-notice">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <CreditCard className="h-4 w-4 text-cyan-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-cyan-300">SMS uses a separate credit system</p>
              <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">
                SMS credits are billed separately from your monthly subscription at <span className="text-gray-900 font-medium">$0.049 per 160-character message</span>. A 3.55% processing fee applies at checkout.
              </p>
            </div>
          </div>
          {!isLow && (
            <Button onClick={openRecharge} variant="outline" size="sm" className="text-xs h-7 border-cyan-800 text-cyan-400 hover:bg-cyan-50 flex-shrink-0" data-testid="recharge-btn">
              <Plus className="h-3 w-3 mr-1" /> Recharge
            </Button>
          )}
        </div>
      </div>

      {/* Auto-Recharge Settings */}
      {autoRecharge && (
        <div className="mt-3 bg-[#0d1b2a] border border-gray-200/50 rounded-lg px-4 py-3" data-testid="auto-recharge-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-400" />
              <div>
                <p className="text-xs font-semibold text-gray-900">Auto-Recharge</p>
                <p className="text-[10px] text-gray-500">
                  {autoRecharge.enabled
                    ? `Active — recharges ${autoRecharge.tier_id === 'sms_25' ? '$25' : autoRecharge.tier_id === 'sms_50' ? '$50' : '$100'} when balance drops below $${autoRecharge.threshold?.toFixed(0)}`
                    : autoRecharge.has_payment_method
                      ? 'Disabled — toggle on to never run out of credits'
                      : 'Save a card to enable automatic top-ups'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {autoRecharge.has_payment_method ? (
                <>
                  <span className="text-[10px] text-gray-500">
                    {autoRecharge.card_brand?.toUpperCase()} ****{autoRecharge.card_last4}
                  </span>
                  <button
                    onClick={() => updateAutoRecharge({ enabled: !autoRecharge.enabled })}
                    disabled={savingAuto}
                    className={`relative w-9 h-5 rounded-full transition-colors ${autoRecharge.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                    data-testid="auto-recharge-toggle"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow ${autoRecharge.enabled ? 'translate-x-4' : ''}`} />
                  </button>
                </>
              ) : (
                <Button
                  onClick={handleSetupAutoRecharge}
                  disabled={savingAuto}
                  variant="outline"
                  size="sm"
                  className="text-[10px] h-6 border-amber-700/50 text-amber-400 hover:bg-amber-50"
                  data-testid="setup-auto-recharge-btn"
                >
                  {savingAuto ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save Card'}
                </Button>
              )}
            </div>
          </div>
          {/* Settings row when enabled */}
          {autoRecharge.enabled && autoRecharge.has_payment_method && (
            <div className="mt-2 pt-2 border-t border-gray-200/30 flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-500">Recharge when below</span>
                <select
                  value={autoRecharge.threshold || 5}
                  onChange={(e) => updateAutoRecharge({ threshold: parseFloat(e.target.value) })}
                  className="bg-gray-50 border border-gray-200 text-gray-900 text-[10px] rounded px-1.5 py-0.5"
                  data-testid="auto-threshold-select"
                >
                  <option value="3">$3</option>
                  <option value="5">$5</option>
                  <option value="10">$10</option>
                  <option value="25">$25</option>
                  <option value="50">$50</option>
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-500">Recharge amount</span>
                <select
                  value={autoRecharge.tier_id || 'sms_25'}
                  onChange={(e) => updateAutoRecharge({ tier_id: e.target.value })}
                  className="bg-gray-50 border border-gray-200 text-gray-900 text-[10px] rounded px-1.5 py-0.5"
                  data-testid="auto-tier-select"
                >
                  <option value="sms_25">$25</option>
                  <option value="sms_50">$50</option>
                  <option value="sms_100">$100</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recharge Modal */}
      {showRecharge && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowRecharge(false)}>
          <div className="bg-white border border-gray-200 rounded-xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="recharge-modal">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-cyan-400" />
                </div>
                <h3 className="text-base font-bold text-gray-900">Recharge SMS Credits</h3>
              </div>
              <button onClick={() => setShowRecharge(false)} className="text-gray-500 hover:text-gray-900">
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="text-xs text-gray-400 mb-4">
              Current balance: <span className={`font-semibold ${isLow ? 'text-red-400' : 'text-gray-900'}`}>${balance.toFixed(2)}</span>
              {' '} ({stats.sms_remaining} messages remaining)
            </p>

            {loadingTiers ? (
              <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" /></div>
            ) : (
              <>
                <div className="space-y-2 mb-5">
                  {tiers.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTier(t.id)}
                      className={`w-full text-left rounded-lg p-3 border transition-all ${
                        selectedTier === t.id
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-600'
                      }`}
                      data-testid={`tier-${t.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">${t.credit_amount.toFixed(0)} Credits</p>
                          <p className="text-[10px] text-gray-500">{t.sms_count.toLocaleString()} messages</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-900 font-medium">${t.total_charge.toFixed(2)}</p>
                          <p className="text-[10px] text-gray-500">incl. {t.fee_percent}% fee</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Fee breakdown */}
                {selected && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-5 space-y-1.5" data-testid="fee-breakdown">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">SMS Credits</span>
                      <span className="text-gray-900">${selected.credit_amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Processing Fee ({selected.fee_percent}%)</span>
                      <span className="text-gray-900">${selected.processing_fee.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-1.5 flex justify-between text-xs font-semibold">
                      <span className="text-gray-300">Total Charge</span>
                      <span className="text-cyan-400">${selected.total_charge.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Button
                    onClick={() => handleCheckout('stripe')}
                    disabled={processing || !selectedTier}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-sm h-10"
                    data-testid="checkout-stripe-btn"
                  >
                    {processing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><CreditCard className="h-4 w-4 mr-2" /> Pay with Card — ${selected?.total_charge.toFixed(2)}</>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleCheckout('paypal')}
                    disabled={processing || !selectedTier}
                    className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-sm h-10 text-gray-900"
                    data-testid="checkout-paypal-btn"
                  >
                    {processing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><DollarSign className="h-4 w-4 mr-2" /> Pay with PayPal — ${selected?.total_charge.toFixed(2)}</>
                    )}
                  </Button>
                </div>

                <p className="text-[10px] text-gray-600 text-center mt-3">
                  Secure payment via Stripe or PayPal. Credits are applied instantly.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InboxView({ getHeaders, hdrs }) {
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/sms/conversations`, { headers: getHeaders() })
      .then(r => r.json()).then(d => setConvos(d.conversations || []))
      .catch(console.error).finally(() => setLoading(false));
  }, [getHeaders]);

  const openConvo = async (phone) => {
    setSelected(phone);
    const res = await fetch(`${API}/api/client/dashboard/sms/conversations/${encodeURIComponent(phone)}`, { headers: getHeaders() });
    const d = await res.json();
    setMessages(d.messages || []);
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    await fetch(`${API}/api/client/dashboard/sms/send`, {
      method: 'POST', headers: hdrs(),
      body: JSON.stringify({ to: selected, message: reply }),
    });
    setReply('');
    setSending(false);
    openConvo(selected);
  };

  if (loading) return <div className="text-gray-400 animate-pulse text-center py-8 text-sm">Loading...</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" style={{ minHeight: 400 }} data-testid="sms-inbox">
      {/* Conversation list */}
      <div className="space-y-1 md:border-r md:border-gray-200 md:pr-3">
        {convos.length === 0 && <p className="text-gray-500 text-sm text-center py-8">No conversations yet</p>}
        {convos.map(c => (
          <button key={c.phone} onClick={() => openConvo(c.phone)}
            className={`w-full text-left px-3 py-3 rounded-lg transition-colors ${selected === c.phone ? 'bg-blue-600/15 border border-blue-500/30' : 'bg-white border border-gray-200 hover:border-gray-200'}`}
            data-testid={`convo-${c.phone}`}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-900/30 flex items-center justify-center text-blue-400 text-xs font-bold flex-shrink-0">
                {(c.contact_name || c.phone).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-900 font-medium truncate">{c.contact_name || c.phone}</p>
                  {c.unread > 0 && <Badge className="bg-blue-600 text-gray-900 text-[9px] ml-1">{c.unread}</Badge>}
                </div>
                <p className="text-[10px] text-gray-500 truncate">{c.last_message}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Message thread */}
      <div className="md:col-span-2 flex flex-col">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">Select a conversation</div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-2 max-h-[350px] mb-3 p-2">
              {messages.map((m, i) => (
                <div key={m.message_id || `${m.created_at}-${i}`} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm ${
                    m.direction === 'outbound' ? 'bg-blue-600 text-gray-900' : 'bg-gray-800 text-gray-200'
                  }`}>
                    <p>{m.message}</p>
                    <p className="text-[9px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={reply} onChange={e => setReply(e.target.value)} placeholder="Type a message..."
                className="bg-gray-50 border-gray-200 text-gray-900 text-sm flex-1" data-testid="sms-reply-input"
                onKeyDown={e => e.key === 'Enter' && sendReply()} />
              <Button onClick={sendReply} disabled={sending || !reply.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="sms-reply-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SendView({ getHeaders, hdrs }) {
  const [form, setForm] = useState({ to: '', message: '', contact_name: '' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    setResult(null);
    const res = await fetch(`${API}/api/client/dashboard/sms/send`, {
      method: 'POST', headers: hdrs(), body: JSON.stringify(form),
    });
    const d = await res.json();
    setResult(d);
    setSending(false);
    if (d.success) setForm({ to: '', message: '', contact_name: '' });
  };

  return (
    <div data-testid="sms-send-view">
      <Card className="bg-white border-gray-200">
        <CardContent className="pt-5 pb-5">
          <form onSubmit={send} className="space-y-3 max-w-lg">
            <div>
              <Label className="text-xs text-gray-400">Phone Number *</Label>
              <Input value={form.to} onChange={e => setForm({...form, to: e.target.value})} required
                placeholder="+18175551234" className="bg-gray-50 border-gray-200 text-gray-900 text-sm mt-1" data-testid="sms-to-phone" />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Contact Name</Label>
              <Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})}
                placeholder="John Doe" className="bg-gray-50 border-gray-200 text-gray-900 text-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Message *</Label>
              <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} required rows={3}
                placeholder="Type your message..." className="w-full mt-1 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-md px-3 py-2 resize-none placeholder:text-gray-600" data-testid="sms-message" />
              <p className="text-[10px] text-gray-500 mt-1">{form.message.length}/160 characters</p>
            </div>
            <Button type="submit" disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-sm" data-testid="sms-send-btn">
              {sending ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Sending...</> : <><Send className="h-3 w-3 mr-1" /> Send SMS</>}
            </Button>
          </form>
          {result && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${result.success ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'}`}>
              {result.success ? 'Message sent successfully!' : result.detail || 'Failed to send message'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ContactsView({ getHeaders, hdrs }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ phone: '', first_name: '', last_name: '', email: '', tags: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tags, setTags] = useState([]);
  const [filterTag, setFilterTag] = useState('');

  const load = useCallback(() => {
    const url = filterTag
      ? `${API}/api/client/dashboard/sms/contacts?tag=${encodeURIComponent(filterTag)}`
      : `${API}/api/client/dashboard/sms/contacts`;
    fetch(url, { headers: getHeaders() })
      .then(r => r.json()).then(d => setContacts(d.contacts || []))
      .catch(console.error).finally(() => setLoading(false));
  }, [getHeaders, filterTag]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/sms/contacts/tags`, { headers: getHeaders() })
      .then(r => r.json()).then(d => setTags(d.tags || [])).catch(() => {});
  }, [getHeaders]);

  const resetForm = () => { setForm({ phone: '', first_name: '', last_name: '', email: '', tags: '', notes: '' }); setShowAdd(false); setEditing(null); };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
    try {
      if (editing) {
        await fetch(`${API}/api/client/dashboard/sms/contacts/${editing}`, {
          method: 'PUT', headers: hdrs(), body: JSON.stringify(payload),
        });
      } else {
        const res = await fetch(`${API}/api/client/dashboard/sms/contacts`, {
          method: 'POST', headers: hdrs(), body: JSON.stringify(payload),
        });
        if (!res.ok) { const d = await res.json(); alert(d.detail || 'Error'); }
      }
      resetForm(); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    await fetch(`${API}/api/client/dashboard/sms/contacts/${id}`, { method: 'DELETE', headers: hdrs() });
    load();
  };

  const startEdit = (c) => {
    setEditing(c.contact_id);
    setForm({ phone: c.phone, first_name: c.first_name || '', last_name: c.last_name || '', email: c.email || '', tags: (c.tags || []).join(', '), notes: c.notes || '' });
    setShowAdd(true);
  };

  const filtered = contacts.filter(c => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (c.phone || '').includes(s) || (c.first_name || '').toLowerCase().includes(s) || (c.last_name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s);
  });

  if (loading) return <div className="text-gray-400 animate-pulse text-center py-8 text-sm">Loading...</div>;

  return (
    <div className="space-y-3" data-testid="sms-contacts-view">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 items-center flex-1 min-w-0">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-2 h-3.5 w-3.5 text-gray-500" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search contacts..."
              className="pl-9 bg-gray-50 border-gray-200 text-gray-900 text-xs h-8" data-testid="contacts-search" />
          </div>
          {tags.length > 0 && (
            <select value={filterTag} onChange={e => setFilterTag(e.target.value)}
              className="bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-2 h-8">
              <option value="">{t('sms.allTags')}</option>
              {tags.map(t => <option key={t.tag} value={t.tag}>{t.tag} ({t.count})</option>)}
            </select>
          )}
        </div>
        <Button size="sm" onClick={() => { resetForm(); setShowAdd(true); }} className="bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="add-contact-btn">
          <Plus className="h-3 w-3 mr-1" /> Add Contact
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-900">{editing ? 'Edit Contact' : 'New Contact'}</p>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-900"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-[10px] text-gray-500">Phone *</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+18175551234" className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" data-testid="contact-phone" /></div>
              <div><Label className="text-[10px] text-gray-500">First Name</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
              <div><Label className="text-[10px] text-gray-500">Last Name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
              <div><Label className="text-[10px] text-gray-500">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
              <div><Label className="text-[10px] text-gray-500">Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="vip, customer" className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" data-testid="contact-tags" /></div>
              <div><Label className="text-[10px] text-gray-500">Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
            </div>
            <Button onClick={save} disabled={saving || !form.phone} className="bg-blue-600 hover:bg-blue-700 text-xs mt-3" data-testid="contact-save-btn">
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
              {editing ? 'Update' : 'Add Contact'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Contacts Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="pt-3 pb-3 px-0">
          {filtered.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No contacts yet. Add your first contact above.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-gray-500 px-4 py-2 font-medium">Name</th>
                    <th className="text-left text-gray-500 px-4 py-2 font-medium">Phone</th>
                    <th className="text-left text-gray-500 px-4 py-2 font-medium hidden md:table-cell">Email</th>
                    <th className="text-left text-gray-500 px-4 py-2 font-medium hidden lg:table-cell">Tags</th>
                    <th className="text-left text-gray-500 px-4 py-2 font-medium">Consent</th>
                    <th className="text-right text-gray-500 px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.contact_id} className="border-b border-gray-200/50 hover:bg-white/[0.02]" data-testid={`contact-row-${c.contact_id}`}>
                      <td className="px-4 py-2.5 text-gray-900">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-300 font-mono">{c.phone}</td>
                      <td className="px-4 py-2.5 text-gray-400 hidden md:table-cell">{c.email || '—'}</td>
                      <td className="px-4 py-2.5 hidden lg:table-cell">
                        <div className="flex gap-1 flex-wrap">
                          {(c.tags || []).map(t => <Badge key={t} className="bg-blue-900/30 text-blue-400 text-[9px]">{t}</Badge>)}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {c.sms_consent_verified ? <Badge className="bg-green-50 text-green-400 text-[9px]">Verified</Badge>
                          : c.sms_consent_pending ? <Badge className="bg-amber-900/30 text-amber-400 text-[9px]">Pending</Badge>
                          : <Badge className="bg-gray-800 text-gray-500 text-[9px]">None</Badge>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => startEdit(c)} className="text-gray-500 hover:text-blue-400 p-1" data-testid={`edit-contact-${c.contact_id}`}><Edit2 className="h-3 w-3" /></button>
                          <button onClick={() => remove(c.contact_id)} className="text-gray-500 hover:text-red-400 p-1" data-testid={`delete-contact-${c.contact_id}`}><Trash2 className="h-3 w-3" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[10px] text-gray-600 px-4 pt-2">{filtered.length} contact{filtered.length !== 1 ? 's' : ''}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function TemplatesView({ getHeaders, hdrs }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', text: '', category: 'general' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/client/dashboard/sms/templates`, { headers: getHeaders() })
      .then(r => r.json()).then(d => setTemplates(d.templates || []))
      .catch(console.error).finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ name: '', text: '', category: 'general' }); setShowAdd(false); setEditing(null); };

  const save = async () => {
    if (!form.name || !form.text) return;
    setSaving(true);
    try {
      if (editing) {
        await fetch(`${API}/api/client/dashboard/sms/templates/${editing}`, {
          method: 'PUT', headers: hdrs(), body: JSON.stringify(form),
        });
      } else {
        await fetch(`${API}/api/client/dashboard/sms/templates`, {
          method: 'POST', headers: hdrs(), body: JSON.stringify(form),
        });
      }
      resetForm(); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await fetch(`${API}/api/client/dashboard/sms/templates/${id}`, { method: 'DELETE', headers: hdrs() });
    load();
  };

  const startEdit = (t) => {
    setEditing(t.template_id);
    setForm({ name: t.name, text: t.text, category: t.category || 'general' });
    setShowAdd(true);
  };

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'appointment', label: 'Appointment' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'follow_up', label: 'Follow-Up' },
    { value: 'reminder', label: 'Reminder' },
  ];

  if (loading) return <div className="text-gray-400 animate-pulse text-center py-8 text-sm">Loading...</div>;

  return (
    <div className="space-y-3" data-testid="sms-templates-view">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">{templates.length} template{templates.length !== 1 ? 's' : ''}</p>
        <Button size="sm" onClick={() => { resetForm(); setShowAdd(true); }} className="bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="add-template-btn">
          <Plus className="h-3 w-3 mr-1" /> New Template
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-900">{editing ? 'Edit Template' : 'New Template'}</p>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-900"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-gray-500">Template Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Appointment Reminder" className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" data-testid="template-name" /></div>
                <div>
                  <Label className="text-[10px] text-gray-500">Category</Label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                    className="w-full mt-0.5 bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-2 h-8" data-testid="template-category">
                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Message Text *</Label>
                <textarea value={form.text} onChange={e => setForm({...form, text: e.target.value})} rows={3}
                  placeholder="Hi {name}, this is a reminder about your appointment on {date}. Reply STOP to opt out."
                  className="w-full mt-0.5 bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-3 py-2 resize-none placeholder:text-gray-600" data-testid="template-text" />
                <p className="text-[10px] text-gray-600 mt-1">{form.text.length}/160 chars &middot; Use {'{name}'}, {'{date}'}, {'{business}'} as variables</p>
              </div>
              <Button onClick={save} disabled={saving || !form.name || !form.text} className="bg-blue-600 hover:bg-blue-700 text-xs" data-testid="template-save-btn">
                {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                {editing ? 'Update' : 'Create Template'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates Grid */}
      {templates.length === 0 && !showAdd ? (
        <Card className="bg-white border-gray-200">
          <CardContent className="py-8 text-center">
            <FileText className="h-8 w-8 text-gray-700 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No templates yet</p>
            <p className="text-[10px] text-gray-600 mt-1">Create reusable SMS templates to save time</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map(t => (
            <Card key={t.template_id} className="bg-white border-gray-200 hover:border-gray-200 transition-colors" data-testid={`template-card-${t.template_id}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.name}</p>
                    <Badge className="bg-gray-800 text-gray-400 text-[9px] mt-1">{t.category || 'general'}</Badge>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(t)} className="text-gray-500 hover:text-blue-400 p-1" data-testid={`edit-template-${t.template_id}`}><Edit2 className="h-3 w-3" /></button>
                    <button onClick={() => remove(t.template_id)} className="text-gray-500 hover:text-red-400 p-1" data-testid={`delete-template-${t.template_id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed">{t.text}</p>
                {t.variables && t.variables.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {t.variables.map(v => <Badge key={v} className="bg-blue-50 text-blue-400 text-[9px]">{`{${v}}`}</Badge>)}
                  </div>
                )}
                <p className="text-[9px] text-gray-600 mt-2">{t.text.length} chars</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function OptInView({ getHeaders, hdrs }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/sms/opt-in/status`, { headers: getHeaders() })
      .then(r => r.json()).then(setStatus).catch(console.error);
  }, [getHeaders]);

  if (!status) return <div className="text-gray-400 animate-pulse text-center py-8">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="sms-optin-view">
      <Card className="bg-white border-gray-200">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-5 w-5 text-blue-400" />
            <div>
              <p className="text-sm font-semibold text-gray-900">TCPA Double Opt-In Status</p>
              <p className="text-[10px] text-gray-500">Compliant subscriber management</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Contacts', value: status.total, icon: Users, color: 'text-blue-400' },
              { label: 'Verified', value: status.verified, icon: CheckCircle, color: 'text-green-400' },
              { label: 'Pending', value: status.pending, icon: Clock, color: 'text-amber-400' },
              { label: 'Opted Out', value: status.opted_out, icon: AlertCircle, color: 'text-red-400' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <s.icon className={`h-5 w-5 mx-auto mb-1 ${s.color}`} />
                <p className="text-lg font-bold text-gray-900">{s.value}</p>
                <p className="text-[10px] text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <div className="bg-blue-900/10 border border-blue-500/20 rounded-lg px-4 py-3">
        <p className="text-xs text-blue-300">
          The double opt-in flow automatically handles YES/STOP/HELP keywords via inbound webhook. Contacts must reply YES before receiving bulk messages.
        </p>
      </div>
    </div>
  );
}

function TenDLCView({ getHeaders, hdrs }) {
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    legal_business_name: '', ein: '', business_address: '', business_city: '',
    business_state: '', business_zip: '', business_phone: '',
    authorized_contact_name: '', authorized_contact_email: '', authorized_contact_phone: '',
    use_case: '', sample_messages: '', website: '', vertical: '',
  });

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/sms/10dlc`, { headers: getHeaders() })
      .then(r => r.json()).then(d => {
        setReg(d.registration);
        if (d.registration && d.registration.status !== 'not_started') {
          setForm(prev => ({ ...prev, ...d.registration }));
        }
      })
      .catch(console.error).finally(() => setLoading(false));
  }, [getHeaders]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    await fetch(`${API}/api/client/dashboard/sms/10dlc`, {
      method: 'PUT', headers: hdrs(), body: JSON.stringify(form),
    });
    setSaving(false);
  };

  if (loading) return <div className="text-gray-400 animate-pulse text-center py-8">Loading...</div>;

  return (
    <div data-testid="sms-10dlc-view">
      <Card className="bg-white border-gray-200">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-5 w-5 text-blue-400" />
            <div>
              <p className="text-sm font-semibold text-gray-900">10DLC Registration</p>
              <p className="text-[10px] text-gray-500">Required for A2P SMS compliance</p>
            </div>
            {reg?.status && reg.status !== 'not_started' && (
              <Badge className={`ml-auto text-[10px] ${reg.status === 'approved' ? 'bg-green-50 text-green-400' : reg.status === 'submitted' ? 'bg-amber-900/30 text-amber-400' : 'bg-gray-800 text-gray-400'}`}>
                {reg.status}
              </Badge>
            )}
          </div>

          <form onSubmit={save} className="space-y-4">
            <div>
              <p className="text-xs text-gray-400 font-medium mb-2">Business Information</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-gray-500">Legal Business Name *</Label><Input value={form.legal_business_name} onChange={e => setForm({...form, legal_business_name: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
                <div><Label className="text-[10px] text-gray-500">EIN (Tax ID) *</Label><Input value={form.ein} onChange={e => setForm({...form, ein: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" placeholder="XX-XXXXXXX" /></div>
                <div className="md:col-span-2"><Label className="text-[10px] text-gray-500">Business Address *</Label><Input value={form.business_address} onChange={e => setForm({...form, business_address: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
                <div><Label className="text-[10px] text-gray-500">City *</Label><Input value={form.business_city} onChange={e => setForm({...form, business_city: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-gray-500">State *</Label><Input value={form.business_state} onChange={e => setForm({...form, business_state: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
                  <div><Label className="text-[10px] text-gray-500">ZIP *</Label><Input value={form.business_zip} onChange={e => setForm({...form, business_zip: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
                </div>
                <div><Label className="text-[10px] text-gray-500">Business Phone *</Label><Input value={form.business_phone} onChange={e => setForm({...form, business_phone: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
                <div><Label className="text-[10px] text-gray-500">Website</Label><Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-400 font-medium mb-2">Authorized Contact</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-[10px] text-gray-500">Full Name *</Label><Input value={form.authorized_contact_name} onChange={e => setForm({...form, authorized_contact_name: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
                <div><Label className="text-[10px] text-gray-500">Email *</Label><Input value={form.authorized_contact_email} onChange={e => setForm({...form, authorized_contact_email: e.target.value})} required type="email" className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
                <div><Label className="text-[10px] text-gray-500">Phone *</Label><Input value={form.authorized_contact_phone} onChange={e => setForm({...form, authorized_contact_phone: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-400 font-medium mb-2">Use Case Details</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-[10px] text-gray-500">Business Vertical *</Label>
                  <select value={form.vertical} onChange={e => setForm({...form, vertical: e.target.value})} required
                    className="w-full mt-0.5 bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-2 h-8">
                    <option value="">{t('sms.selectOption')}</option>
                    <option value="home_services">{t('sms.homeServices')}</option>
                    <option value="healthcare">{t('sms.healthcare')}</option>
                    <option value="real_estate">{t('sms.realEstate')}</option>
                    <option value="automotive">{t('sms.automotive')}</option>
                    <option value="food_beverage">{t('sms.foodBeverage')}</option>
                    <option value="retail">{t('sms.retail')}</option>
                    <option value="professional_services">{t('sms.professionalServices')}</option>
                    <option value="other">{t('sms.other')}</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Use Case Description *</Label>
                  <textarea value={form.use_case} onChange={e => setForm({...form, use_case: e.target.value})} required rows={3}
                    placeholder="Describe how you'll use SMS (e.g., appointment reminders, order updates, marketing promotions)..."
                    className="w-full mt-0.5 bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-3 py-2 resize-none placeholder:text-gray-600" />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Sample Messages (2-3 examples) *</Label>
                  <textarea value={form.sample_messages} onChange={e => setForm({...form, sample_messages: e.target.value})} required rows={3}
                    placeholder="Example 1: Hi {{name}}, your appointment is confirmed for {{date}} at {{time}}.&#10;Example 2: {{business}} special: 20% off all services this week! Reply STOP to opt out."
                    className="w-full mt-0.5 bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-3 py-2 resize-none placeholder:text-gray-600" />
                </div>
              </div>
            </div>

            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-sm" data-testid="10dlc-submit">
              {saving ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving...</> : 'Submit Registration'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
